/**
 * G9 / Stage 3 A6 — shop catalog publishes place name for CustomerShellHeader center.
 * Default null → hubs keep tab titles (Nakupovat / Oblíbené / …).
 */
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

const ShellPlaceTitleValueContext = createContext<string | null>(null);
const ShellPlaceTitleSetContext = createContext<
  ((title: string | null) => void) | null
>(null);

export function ShellPlaceTitleProvider({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const [placeTitle, setPlaceTitleState] = useState<string | null>(null);
  const setPlaceTitle = useCallback((next: string | null): void => {
    setPlaceTitleState((prev) => (prev === next ? prev : next));
  }, []);
  return (
    <ShellPlaceTitleSetContext.Provider value={setPlaceTitle}>
      <ShellPlaceTitleValueContext.Provider value={placeTitle}>
        {children}
      </ShellPlaceTitleValueContext.Provider>
    </ShellPlaceTitleSetContext.Provider>
  );
}

export function useShellPlaceTitle(): string | null {
  return useContext(ShellPlaceTitleValueContext);
}

/**
 * Publish place title while mounted on a sales-point catalog screen.
 * Clears on unmount so hubs revert to tab labels.
 *
 * G3: clear ONLY on publisher unmount — never in the same effect that depends
 * on `placeTitle`. Coupling cleanup to the title dep flashed `null` (shell
 * interim `shop.title` / Obchod) between slug fallback → meta name updates and
 * under StrictMode remount churn; shop-smoke 430 captured that flash while 375
 * often settled on Railway Cafe.
 */
export function usePublishShellPlaceTitle(placeTitle: string | null): void {
  const setPlaceTitle = useContext(ShellPlaceTitleSetContext);
  const publishedTitleRef = useRef<string | null | undefined>(undefined);
  useLayoutEffect(() => {
    if (setPlaceTitle === null) {
      return;
    }
    // G9 — skip shell center churn when slug fallback → meta name resolves to same string.
    if (publishedTitleRef.current === placeTitle) {
      return;
    }
    publishedTitleRef.current = placeTitle;
    setPlaceTitle(placeTitle);
  }, [setPlaceTitle, placeTitle]);

  useLayoutEffect(() => {
    if (setPlaceTitle === null) {
      return;
    }
    return () => {
      publishedTitleRef.current = undefined;
      setPlaceTitle(null);
    };
  }, [setPlaceTitle]);
}

/** Test / rare callers that need both — prefer the hooks above. */
export function useShellPlaceTitleControls(): {
  readonly placeTitle: string | null;
  readonly setPlaceTitle: ((title: string | null) => void) | null;
} {
  const placeTitle = useContext(ShellPlaceTitleValueContext);
  const setPlaceTitle = useContext(ShellPlaceTitleSetContext);
  return useMemo(
    () => ({ placeTitle, setPlaceTitle }),
    [placeTitle, setPlaceTitle],
  );
}
